(() => {
  const closeDetails = (details) => {
    if (details && details.open) details.open = false;
  };

  const inlineVideos = new WeakSet();
  let activeFullscreenVideo = null;

  const hydrateVideo = (video, shouldPlay = true) => {
    if (!video) return;

    if (!video.dataset.gv2VideoLoaded) {
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.gv2VideoLoaded = 'true';
      video.load();
    }

    if (shouldPlay) {
      video.muted = true;
      video.defaultMuted = true;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    }
  };

  const enterFallbackFullscreen = (video) => {
    const wrapper = video?.closest('[data-gv2-inline-video]');
    if (!wrapper) return;

    wrapper.classList.add('gv2-inline-video--fullscreen');
    document.documentElement.classList.add('gv2-video-fullscreen-active');
    wrapper.querySelector('[data-gv2-video-close]')?.focus();
  };

  const exitFallbackFullscreen = (video) => {
    const wrapper = video?.closest('[data-gv2-inline-video]');
    if (!wrapper) return;

    wrapper.classList.remove('gv2-inline-video--fullscreen');
    document.documentElement.classList.remove('gv2-video-fullscreen-active');
  };

  const restoreInlineVideo = (video) => {
    if (!video) return;

    exitFallbackFullscreen(video);
    video.controls = false;
    video.removeAttribute('controls');
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.autoplay = true;
    video.setAttribute('autoplay', '');

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    if (activeFullscreenVideo === video) activeFullscreenVideo = null;
  };

  const openVideoFullscreen = (video) => {
    if (!video) return;

    hydrateVideo(video, false);
    video.controls = true;
    video.setAttribute('controls', '');
    video.muted = false;
    video.defaultMuted = false;
    video.removeAttribute('muted');
    activeFullscreenVideo = video;

    let nativeFullscreenRequested = false;
    try {
      if (typeof video.requestFullscreen === 'function') {
        nativeFullscreenRequested = true;
        const fullscreenPromise = video.requestFullscreen();
        if (fullscreenPromise && typeof fullscreenPromise.catch === 'function') {
          fullscreenPromise.catch(() => enterFallbackFullscreen(video));
        }
      } else if (typeof video.webkitEnterFullscreen === 'function') {
        nativeFullscreenRequested = true;
        video.webkitEnterFullscreen();
      }
    } catch (error) {
      nativeFullscreenRequested = false;
    }

    if (!nativeFullscreenRequested) enterFallbackFullscreen(video);

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
  };

  const lazyVideoObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            hydrateVideo(video);
          } else if (video !== activeFullscreenVideo) {
            video.pause();
          }
        });
      }, { rootMargin: '200px 0px', threshold: 0.1 })
    : null;

  const initialiseInlineVideos = (root = document) => {
    root.querySelectorAll('[data-gv2-inline-video] video').forEach((video) => {
      if (inlineVideos.has(video)) return;
      inlineVideos.add(video);
      video.addEventListener('webkitendfullscreen', () => restoreInlineVideo(video));
      if (lazyVideoObserver) lazyVideoObserver.observe(video);
      else hydrateVideo(video);
    });
  };

  initialiseInlineVideos();

  document.addEventListener('shopify:section:load', (event) => {
    initialiseInlineVideos(event.target);
  });

  document.addEventListener('fullscreenchange', () => {
    if (
      !document.fullscreenElement &&
      activeFullscreenVideo &&
      !activeFullscreenVideo.closest('.gv2-inline-video--fullscreen')
    ) {
      restoreInlineVideo(activeFullscreenVideo);
    }
  });

  document.addEventListener('click', (event) => {
    const videoClose = event.target.closest('[data-gv2-video-close]');
    if (videoClose) {
      event.preventDefault();
      restoreInlineVideo(videoClose.closest('[data-gv2-inline-video]')?.querySelector('video'));
      return;
    }

    const videoTrigger = event.target.closest('[data-gv2-video-open]');
    if (videoTrigger) {
      event.preventDefault();
      openVideoFullscreen(videoTrigger.closest('[data-gv2-inline-video]')?.querySelector('video'));
      return;
    }

    document.querySelectorAll('[data-v2-mega][open], [data-v2-dropdown][open]').forEach((details) => {
      if (!details.contains(event.target)) closeDetails(details);
    });

    const closeButton = event.target.closest('[data-v2-drawer-close]');
    if (closeButton) closeDetails(closeButton.closest('[data-v2-drawer]'));
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const fallbackVideo = document.querySelector('.gv2-inline-video--fullscreen video');
    if (fallbackVideo) {
      event.preventDefault();
      restoreInlineVideo(fallbackVideo);
      return;
    }

    const openDetails = document.querySelectorAll('[data-v2-mega][open], [data-v2-dropdown][open], [data-v2-drawer][open]');
    openDetails.forEach((details) => {
      closeDetails(details);
      const summary = details.querySelector(':scope > summary');
      if (summary) summary.focus();
    });
  });

  document.querySelectorAll('[data-v2-mega], [data-v2-dropdown]').forEach((details) => {
    details.addEventListener('toggle', () => {
      if (!details.open) return;
      document.querySelectorAll('[data-v2-mega][open], [data-v2-dropdown][open]').forEach((other) => {
        if (other !== details) closeDetails(other);
      });
    });
  });
})();
